import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { reportsAPI, statusAPI, violationsAPI } from '@/lib/api';
import { formatDurationShort, getMonthStartDate, getMonthEndDate } from '@/lib/utils';
import {
  ArrowLeft,
  Play,
  Pause,
  UtensilsCrossed,
  MapPin,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';

const STATUS_ICONS = {
  working: Play,
  pause: Pause,
  lunch: UtensilsCrossed,
  outside: MapPin,
  no_gps: AlertTriangle,
};
const STATUS_COLORS = {
  working: 'text-emerald-400',
  pause: 'text-amber-400',
  lunch: 'text-cyan-400',
  outside: 'text-white/30',
  no_gps: 'text-rose-400',
};

function getMonthBounds(year, month) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
}

export default function MyHistory() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [dayLog, setDayLog] = useState({});
  const [dayLogLoading, setDayLogLoading] = useState(null);
  const [violations, setViolations] = useState([]);
  const [violationsLoading, setViolationsLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setDays([]);
    setViolations([]);
    setExpandedDay(null);
    try {
      const { start, end } = getMonthBounds(year, month);
      const [hoursRes, violRes] = await Promise.all([
        reportsAPI.myHours(start, end),
        violationsAPI.my({ start_date: start, end_date: end }).catch(() => ({ data: [] })),
      ]);
      const todayStr = new Date().toISOString().slice(0, 10);
      const sorted = [...(hoursRes.data || [])]
        .filter(d => d.date <= todayStr)
        .sort((a, b) => b.date.localeCompare(a.date));
      setDays(sorted);
      setViolations(violRes.data || []);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleDay = async (date) => {
    if (expandedDay === date) {
      setExpandedDay(null);
      return;
    }
    setExpandedDay(date);
    if (!dayLog[date]) {
      setDayLogLoading(date);
      try {
        const res = await statusAPI.dayLog(date);
        setDayLog(prev => ({ ...prev, [date]: res.data }));
      } catch {
        setDayLog(prev => ({ ...prev, [date]: null }));
      } finally {
        setDayLogLoading(null);
      }
    }
  };

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const n = new Date();
    if (year > n.getFullYear() || (year === n.getFullYear() && month >= n.getMonth() + 1)) return;
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(
    language === 'ru' ? 'ru-RU' : language === 'et' ? 'et-EE' : 'en-US',
    { month: 'long', year: 'numeric' }
  );

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold">{t('employee.myHistoryTitle')}</h1>
        </div>

        {/* Month selector */}
        <div className="flex items-center justify-between glass-card rounded-2xl px-4 py-3">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white">
            ‹
          </button>
          <span className="font-semibold capitalize">{monthLabel}</span>
          <button
            onClick={nextMonth}
            disabled={isCurrentMonth}
            className={`p-1.5 rounded-lg transition-colors ${isCurrentMonth ? 'text-white/20 cursor-not-allowed' : 'hover:bg-white/10 text-white/60 hover:text-white'}`}
          >
            ›
          </button>
        </div>

        {/* Days list */}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : days.length === 0 ? (
          <div className="text-center py-10 text-white/30 text-sm">{t('employee.noDataForPeriod')}</div>
        ) : (
          <div className="space-y-2">
            {days.map(day => {
              const dateObj = new Date(day.date + 'T00:00:00');
              const label = dateObj.toLocaleDateString(
                language === 'ru' ? 'ru-RU' : language === 'et' ? 'et-EE' : 'en-US',
                { weekday: 'short', day: 'numeric', month: 'short' }
              );
              const isExpanded = expandedDay === day.date;
              const log = dayLog[day.date];
              const loadingThis = dayLogLoading === day.date;

              return (
                <div key={day.date} className="glass-card rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleDay(day.date)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors"
                  >
                    <span className="text-white/40 w-24 shrink-0 text-xs text-left">{label}</span>
                    <span className="text-emerald-300 font-semibold w-14 shrink-0 text-sm">
                      {formatDurationShort(day.working_seconds || 0)}
                    </span>
                    {day.pause_seconds > 0 && (
                      <span className="text-amber-300/60 text-xs w-12 shrink-0">
                        {formatDurationShort(day.pause_seconds)}
                      </span>
                    )}
                    {day.lunch_seconds > 0 && (
                      <span className="text-cyan-300/60 text-xs w-12 shrink-0">
                        {formatDurationShort(day.lunch_seconds)}
                      </span>
                    )}
                    {day.has_violation && (
                      <AlertTriangle className="h-3.5 w-3.5 text-rose-400 shrink-0" />
                    )}
                    <span className="ml-auto">
                      {isExpanded
                        ? <ChevronUp className="h-4 w-4 text-white/30" />
                        : <ChevronDown className="h-4 w-4 text-white/30" />
                      }
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-white/10 px-4 py-3">
                      {loadingThis ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-white/40" />
                        </div>
                      ) : !log || !log.entries || log.entries.length === 0 ? (
                        <p className="text-white/30 text-xs text-center py-2">{t('employee.historyEmpty')}</p>
                      ) : (
                        <div className="space-y-1">
                          {log.entries.map((entry, idx) => {
                            const Icon = STATUS_ICONS[entry.status] || MapPin;
                            const color = STATUS_COLORS[entry.status] || 'text-white/30';
                            const timeStr = entry.started_at
                              ? new Date(entry.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                              : '--:--';
                            return (
                              <div key={idx} className="flex items-center gap-3 px-2 py-1.5 rounded-lg text-sm bg-white/3">
                                <span className="text-white/30 w-12 shrink-0 font-mono text-xs">{timeStr}</span>
                                <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
                                <span className="flex-1 text-white/60 text-xs capitalize">
                                  {entry.status?.replace('_', ' ')}
                                </span>
                                {entry.object_name && (
                                  <span className="text-white/30 text-xs truncate max-w-[80px]">{entry.object_name}</span>
                                )}
                                <span className="text-white/30 text-xs shrink-0">
                                  {formatDurationShort(entry.duration_seconds || 0)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Violations block */}
        {!loading && (
          <div className="glass-card rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3">
              {t('employee.violations')}
            </h3>
            {violationsLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-white/40" />
            ) : violations.length === 0 ? (
              <p className="text-white/30 text-sm">{t('employee.noViolations')}</p>
            ) : (
              <div className="space-y-2">
                {violations.map((v, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                    <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-white/80">{v.description || v.type}</p>
                      <p className="text-xs text-white/30">{v.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
